// frontend/TwitterAnalytics/components/TwitterConnectionStatus.jsx
/**
 * Twitter Connection Status Header Component
 */

import React from 'react';
import {
  Box, Paper, Avatar, Typography, Chip, IconButton, Tooltip, Button
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  LinkOff as DisconnectIcon,
  CheckCircle as ConnectedIcon,
  Twitter as TwitterIcon
} from '@mui/icons-material';

const TwitterConnectionStatus = ({ status, onRefresh, onDisconnect }) => {
  if (!status?.connected) return null;

  const getTierColor = (tier) => {
    switch (tier) {
      case 'pro': return 'success';
      case 'basic': return 'primary';
      default: return 'default';
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'pro': return 'Pro ($5000/mo)';
      case 'basic': return 'Basic ($100/mo)';
      default: return 'Free Tier';
    }
  };

  return (
    <Paper sx={{ mx: 3, mt: 2, p: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        {/* Left: Profile Info */}
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar 
            src={status.profile_image_url} 
            alt={status.name}
            sx={{ width: 48, height: 48 }}
          >
            <TwitterIcon />
          </Avatar>
          
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                {status.name || status.username}
              </Typography>
              <ConnectedIcon color="success" sx={{ fontSize: 18 }} />
            </Box>
            <Typography variant="body2" color="textSecondary">
              @{status.username}
            </Typography>
          </Box>
        </Box>

        {/* Center: Stats */}
        <Box display="flex" gap={3}>
          <Box textAlign="center">
            <Typography variant="h6">{status.followers_count?.toLocaleString() || '-'}</Typography>
            <Typography variant="caption" color="textSecondary">Followers</Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6">{status.following_count?.toLocaleString() || '-'}</Typography>
            <Typography variant="caption" color="textSecondary">Following</Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6">{status.tweet_count?.toLocaleString() || '-'}</Typography>
            <Typography variant="caption" color="textSecondary">Tweets</Typography>
          </Box>
        </Box>

        {/* Right: Tier & Actions */}
        <Box display="flex" alignItems="center" gap={2}>
          <Box textAlign="right">
            <Chip 
              label={getTierLabel(status.api_tier)}
              color={getTierColor(status.api_tier)}
              size="small"
              sx={{ mb: 0.5 }}
            />
            <Typography variant="caption" display="block" color="textSecondary">
              {status.monthly_tweet_reads || 0} API reads this month
            </Typography>
          </Box>
          
          <Tooltip title="Refresh Status">
            <IconButton onClick={onRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Disconnect">
            <IconButton onClick={onDisconnect} size="small" color="error">
              <DisconnectIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

export default TwitterConnectionStatus;